import React from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ArrowRight, Sparkles, FileCheck2, ScanFace, BadgeCheck } from "lucide-react";
import { TID } from "@/constants/coverifyIds";

export default function Hero() {
  const scrollToDemo = () => {
    const el = document.getElementById("demo");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="top" className="dark-hero relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 bg-dotgrid opacity-[0.08] pointer-events-none" />

      <div className="container-cv relative pt-14 md:pt-24 pb-16 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left copy */}
        <div className="lg:col-span-7 reveal">
          <span className="pill">
            <Sparkles className="h-3.5 w-3.5 text-accent" />
            Companies House &amp; ECCTA ready
          </span>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.05]">
            Client ID verification,{" "}
            <span className="relative inline-block">
              <span className="relative z-10">built for compliance.</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/40 -z-0 rounded-sm" />
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed">
            CoVerify is the connected identity verification product from CoSec Global -
            biometric document checks, liveness, AML and audit-ready records, all from
            one secure dashboard. Designed for UK accountants, company secretaries and ACSPs.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Button
              data-testid={TID.heroStartVerification}
              onClick={scrollToDemo}
              className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-semibold text-base shadow-[0_8px_24px_-6px_rgba(124,194,66,0.55)]"
            >
              Try a Live Verification
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid={TID.heroContactSales}
              className="h-12 px-6 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground font-semibold text-base"
            >
              <a href="/contact">
                Talk to Sales
              </a>
            </Button>
          </div>

          {/* Mini trust stats */}
          <div className="mt-10 grid grid-cols-3 max-w-lg gap-6">
            {[
              { k: "<5 min", v: "Avg. verification" },
              { k: "ISO27001", v: "Certified hosting" },
              { k: "ECCTA", v: "Companies House ready" },
            ].map((s, i) => (
              <div key={i} className="border-l-2 border-accent pl-3">
                <div className="font-display text-xl md:text-2xl font-bold text-primary">{s.k}</div>
                <div className="text-xs md:text-sm text-muted-foreground">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5 relative">
          <div className="relative mx-auto max-w-[440px]">
            {/* Card 1. Dashboard */}
            <div className="light-surface rounded-2xl border border-border bg-white p-5 shadow-[0_30px_60px_-30px_rgba(14,58,95,0.35)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 grid place-items-center">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Verification</div>
                    <div className="font-display text-sm font-bold text-primary">VR-882134</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-accent/15 text-primary px-2.5 py-1">
                  <BadgeCheck className="h-3.5 w-3.5 text-accent" /> Clear
                </span>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                {[
                  { icon: FileCheck2, label: "Document" },
                  { icon: ScanFace, label: "Liveness" },
                  { icon: BadgeCheck, label: "AML" },
                ].map((c, i) => (
                  <div key={i} className="rounded-xl border border-border bg-secondary/60 p-3 text-center">
                    <c.icon className="mx-auto h-5 w-5 text-primary" />
                    <div className="mt-1 text-[11px] font-medium text-primary">{c.label}</div>
                    <div className="mt-1 text-[10px] font-semibold text-accent uppercase tracking-wider">Passed</div>
                  </div>
                ))}
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Compliance score</span>
                  <span className="font-semibold text-primary">98 / 100</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-accent" style={{ width: "98%" }} />
                </div>
              </div>
            </div>

            {/* Card 2. Floating ID */}
            <div className="light-surface float absolute -left-6 -bottom-8 w-56 rounded-xl border border-border bg-white p-3 shadow-xl rotate-[-4deg]">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-md bg-gradient-to-br from-primary to-primary/70" />
                <div className="flex-1">
                  <div className="h-2 w-3/4 rounded-full bg-secondary" />
                  <div className="mt-1.5 h-2 w-1/2 rounded-full bg-secondary" />
                  <div className="mt-1.5 h-2 w-2/3 rounded-full bg-secondary" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
                <BadgeCheck className="h-3 w-3" /> Document verified
              </div>
            </div>

            {/* Card 3. Floating notification */}
            <div className="float absolute -right-4 -top-6 w-52 rounded-xl border border-border bg-primary text-primary-foreground p-3 shadow-xl rotate-[3deg]" style={{ animationDelay: "1.2s" }}>
              <div className="flex items-center gap-2">
                <ScanFace className="h-4 w-4 text-accent" />
                <div className="text-xs font-semibold">Liveness check</div>
              </div>
              <div className="mt-1 text-[11px] text-white/80">Selfie matched ID photo, 99.4% confidence</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
